import { useState, useEffect } from 'react';
import { getClassReportAssignments } from '../../../../services/ReportService';
import { useGetRemoteServiceWrapper } from '../../../hooks/RemoteServiceHooks';
import DateUtils from '../../../../utils/DateUtils';
import Logger from '../../../../utils/Logger';

const ACADEMIC_CURRICULUM_SECTION = 'Academic Curriculum';
const VIZZLE_CLASSIC_SECTION = 'Vizzle Classic';

/**
 * Flattens the assignments response into CollapsibleGroupMultiSelector options.
 *
 * Options are organized in two section layers:
 * - Academic Curriculum: topics with name + unitName
 * - Vizzle Classic: entries missing topic name or unit
 *
 * Under Academic Curriculum, each topic is a group titled "Topic {name}" with subtitle
 * "Grade {grade} Unit {unitName}". Under Vizzle Classic, lessons share a single group.
 *
 * Academic Curriculum appears first; Vizzle Classic last. Lessons within each group
 * are sorted by name.
 */
const mapAssignmentsToOptions = (groups = []) => {
  const normalized = groups.map((group) => {
    const isVizzleClassic = !group.name || !group.unitName;
    return {
      isVizzleClassic,
      section: isVizzleClassic ? VIZZLE_CLASSIC_SECTION : ACADEMIC_CURRICULUM_SECTION,
      groupName: isVizzleClassic ? VIZZLE_CLASSIC_SECTION : `Topic ${group.name}`,
      groupSubtitle: isVizzleClassic
        ? ''
        : `${group.grade ? `Grade ${group.grade} ` : ''}${group.unitName ? `Unit ${group.unitName}` : ''}`.trim(),
      lessons: group.lessons || [],
    };
  });

  normalized.sort((a, b) => {
    if (a.isVizzleClassic !== b.isVizzleClassic) {
      return a.isVizzleClassic ? 1 : -1;
    }
    return a.groupName.localeCompare(b.groupName);
  });

  return normalized.flatMap((group) => (
    [...group.lessons]
      .sort((l1, l2) => (l1.name || '').localeCompare(l2.name || ''))
      .map((lesson) => ({
        key: lesson.id,
        name: lesson.name,
        section: group.section,
        group: group.groupName,
        groupSubtitle: group.groupSubtitle,
      }))
  ));
};

/**
 * Fetches the assignments for the Class Report "Activities" dropdown and maps them
 * into the CollapsibleGroupMultiSelector option shape
 * ({ key, name, section, group, groupSubtitle }).
 *
 * Refetches whenever the selected date range or subjects change.
 *
 * @param {{ startDate: Date, endDate: Date, subjects: Array<number|string> }} params
 */
export const useReportAssignments = ({ startDate, endDate, subjects = [] }) => {
  const { loading, callRemoteServiceWrapper } = useGetRemoteServiceWrapper();
  const [assignmentOptions, setAssignmentOptions] = useState([]);
  const subjectsKey = subjects.join(',');

  const loadAssignments = async () => {
    // Clear stale options so the dropdown doesn't show results from the previous
    // date range / subjects while refetching (or when the criteria are incomplete).
    setAssignmentOptions([]);
    if (!startDate || !endDate || subjects.length === 0) {
      return;
    }
    try {
      await callRemoteServiceWrapper(async () => {
        const criteria = {
          startDate: DateUtils.convertDateToYYYYMMdd(startDate),
          endDate: DateUtils.convertDateToYYYYMMdd(endDate),
          subjects: subjectsKey,
        };
        const result = await getClassReportAssignments(criteria);
        setAssignmentOptions(mapAssignmentsToOptions(result));
      });
    } catch (e) {
      Logger.logWhenDebugModeIsOn(e);
    }
  };

  useEffect(() => {
    loadAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, subjectsKey]);

  return {
    loading,
    assignmentOptions,
  };
};
